import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Tier, Grab, CreateTierRequest, UpdateTierRequest } from '../types';
import tierService from '../services/tierService';
import grabService from '../services/grabService';
import { useFriedhof } from '../contexts/FriedhofContext';
import {
  Button, PageHeader, ContextChip, Modal, Field, Input, DateInput, Select, FormActions,
  LoadingScreen, EmptyState, Icon, inputClass, tableCard, tableScroll, th, td, tableRow,
  Card, FilterStand, filterAktiv,
} from '../components/ui';
import { useStueckweise } from '../utils/stueckweise';
import { ankunft, markiereNeu } from '../utils/ankunft';

const emptyForm = {
  grabId: 0, name: '', art: '', rasse: '', gebDatum: '', sterbeDatum: '',
  gewicht: '', groesse: '', beisetzungsdatum: '', active: true
};

// ISO-Datum (yyyy-mm-dd) -> deutsches Format dd.mm.yyyy, ohne Zeitzonen-Verschiebung
const formatDatum = (iso?: string): string => {
  if (!iso) return '–';
  const [y, m, d] = iso.split('T')[0].split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
};

type SortKey = 'name' | 'art' | 'grab' | 'beisetzung';
type SortDir = 'asc' | 'desc';

const Tiere: React.FC = () => {
  const navigate = useNavigate();
  const { friedhofId, friedhofName } = useFriedhof();
  const [items, setItems] = useState<Tier[]>([]);
  const [graeber, setGraeber] = useState<Grab[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Tier | null>(null);
  const [formData, setFormData] = useState<any>(emptyForm);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const load = useCallback(async () => {
    if (!friedhofId) return;
    try {
      const [allTiere, friedhofGraeber] = await Promise.all([
        tierService.getAll(),
        grabService.getByFriedhof(friedhofId),
      ]);
      setGraeber(friedhofGraeber);
      const grabIds = new Set(friedhofGraeber.map(g => g.id));
      setItems(allTiere.filter(t => grabIds.has(t.grabId)));
    } catch { toast.error('Tiere konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, [friedhofId]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  // gefilterte + sortierte Liste
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? items.filter(t => [t.name, t.art, t.rasse, t.grabNummer]
          .some(v => (v || '').toLowerCase().includes(q)))
      : items;

    const value = (t: Tier): string => {
      switch (sortKey) {
        case 'art': return t.art || '';
        case 'grab': return t.grabNummer || '';
        case 'beisetzung': return t.beisetzungsdatum || '';
        case 'name':
        default: return t.name || '';
      }
    };

    const sorted = [...filtered].sort((a, b) => {
      const cmp = value(a).localeCompare(value(b), 'de', { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [items, search, sortKey, sortDir]);

  // Bei Such-/Sortieränderung zurück auf die erste Seite.
  // Kein Blättern: die Liste läuft durch und lädt beim Scrollen still nach.
  const { sichtbar, Ende } = useStueckweise(visibleItems);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.grabId) { toast.error('Bitte ein Grab wählen'); return; }
    try {
      const data: any = {
        grabId: formData.grabId,
        name: formData.name || undefined,
        art: formData.art || undefined,
        rasse: formData.rasse || undefined,
        gebDatum: formData.gebDatum || undefined,
        sterbeDatum: formData.sterbeDatum || undefined,
        gewicht: formData.gewicht ? parseFloat(formData.gewicht) : undefined,
        groesse: formData.groesse || undefined,
        beisetzungsdatum: formData.beisetzungsdatum || undefined
      };
      if (editing) {
        await tierService.update(editing.id, { ...data, active: formData.active } as UpdateTierRequest);
        markiereNeu(`tier-${editing.id}`);
        toast.success('Tier gespeichert');
      } else {
        const neu = await tierService.create(data as CreateTierRequest);
        markiereNeu(`tier-${neu.id}`);
        toast.success('Tier angelegt');
      }
      resetForm(); await load();
    } catch (error: any) { toast.error(error?.response?.data?.error || 'Tier konnte nicht gespeichert werden'); }
  };

  const resetForm = () => { setFormData(emptyForm); setEditing(null); setShowForm(false); };

  if (loading) return <LoadingScreen />;

  const sortableTh = (key: SortKey, label: string, extra = '') => (
    <th onClick={() => toggleSort(key)} className={`${th} cursor-pointer select-none hover:text-gray-700 ${extra}`}>{label}{sortIndicator(key)}</th>
  );

  return (
    <div className="mx-auto max-w-6xl p-6">
      <PageHeader
        title="Tiere"
        subtitle={`${items.length} ${items.length === 1 ? 'Tier' : 'Tiere'} auf diesem Friedhof`}
        chip={friedhofName ? <ContextChip>{friedhofName}</ContextChip> : undefined}
        actions={<Button onClick={() => setShowForm(true)} icon={<Icon.Plus className="h-4 w-4" />}>Tier anlegen</Button>}
      />

      <Card className="mb-4 flex flex-wrap items-center gap-3 p-3">
        <div className="relative min-w-[220px] flex-1">
          <span className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${search.trim() ? 'text-brand-600' : 'text-gray-400'}`}><Icon.Search className="h-4 w-4" /></span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Name, Art, Rasse oder Grab…"
            className={`${inputClass} pl-9 ${search.trim() ? filterAktiv : ''}`}
          />
        </div>
        <FilterStand treffer={visibleItems.length} gesamt={items.length} einheit="Tiere" aktiv={search.trim() !== ''} onZuruecksetzen={() => setSearch('')} />
      </Card>

      {showForm && (
        <Modal title={editing ? 'Tier bearbeiten' : 'Neues Tier'} onClose={resetForm}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Grab" required hint="Mehrere Tiere pro Grab sind möglich.">
              <Select required value={formData.grabId} onChange={e => setFormData({ ...formData, grabId: parseInt(e.target.value) })}>
                <option value={0}>– Bitte wählen –</option>
                {graeber.map(g => <option key={g.id} value={g.id}>{g.abteilung}/{g.grabnummer}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name"><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} /></Field>
              <Field label="Art"><Input value={formData.art} onChange={e => setFormData({ ...formData, art: e.target.value })} placeholder="Hund, Katze, …" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Rasse"><Input value={formData.rasse} onChange={e => setFormData({ ...formData, rasse: e.target.value })} /></Field>
              <Field label="Größe"><Input value={formData.groesse} onChange={e => setFormData({ ...formData, groesse: e.target.value })} placeholder="z.B. 30 cm / mittel" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Geburtsdatum"><DateInput value={formData.gebDatum} onChange={v => setFormData({ ...formData, gebDatum: v })} /></Field>
              <Field label="Sterbedatum"><DateInput value={formData.sterbeDatum} onChange={v => setFormData({ ...formData, sterbeDatum: v })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Gewicht (kg)"><Input type="number" step="0.01" value={formData.gewicht} onChange={e => setFormData({ ...formData, gewicht: e.target.value })} /></Field>
              <Field label="Beisetzungsdatum"><DateInput value={formData.beisetzungsdatum} onChange={v => setFormData({ ...formData, beisetzungsdatum: v })} /></Field>
            </div>
            {editing && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={formData.active} onChange={e => setFormData({ ...formData, active: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                Aktiv
              </label>
            )}
            <FormActions>
              <Button type="button" variant="secondary" onClick={resetForm}>Abbrechen</Button>
              <Button type="submit">{editing ? 'Speichern' : 'Anlegen'}</Button>
            </FormActions>
          </form>
        </Modal>
      )}

      <div className={tableCard}>
        <div className={tableScroll}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                {sortableTh('name', 'Tier')}
                <th className={`${th} hidden md:table-cell`}>Besitzer</th>
                {sortableTh('grab', 'Grab')}
                {sortableTh('beisetzung', 'Beisetzung')}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sichtbar.map(item => { const a = ankunft(`tier-${item.id}`); return (
                <tr
                  key={item.id}
                  ref={a.ref}
                  className={`${tableRow} cursor-pointer ${a.className}`}
                  onClick={() => navigate(`/tiere/${item.id}`)}
                >
                  <td className={td}>
                    <span className="block font-medium text-gray-900">{item.name || 'Ohne Namen'}</span>
                    {(item.art || item.rasse) && (
                      <span className="block text-xs text-gray-500">{[item.art, item.rasse].filter(Boolean).join(', ')}</span>
                    )}
                  </td>
                  <td className={`${td} text-gray-700 hidden md:table-cell`}>{item.kundeName}</td>
                  <td className={`${td} whitespace-nowrap tabular-nums text-gray-700`}>{item.grabNummer}</td>
                  <td className={`${td} whitespace-nowrap tabular-nums text-gray-500`}>{item.beisetzungsdatum ? formatDatum(item.beisetzungsdatum) : ''}</td>
                </tr>
              ); })}
            </tbody>
          </table>
          {Ende}
        </div>
        {visibleItems.length === 0 && (
          items.length === 0 ? (
            <EmptyState
              icon={<Icon.Paw />}
              title="Noch keine Tiere auf diesem Friedhof"
              hint="Hier stehen alle beigesetzten Tiere, jeweils mit ihrem Grab."
              action={<Button onClick={() => setShowForm(true)} icon={<Icon.Plus className="h-4 w-4" />}>Tier anlegen</Button>}
            />
          ) : (
            <EmptyState
              icon={<Icon.Paw />}
              title="Keine Treffer für diese Suche"
              action={<Button variant="secondary" onClick={() => setSearch('')}>Suche zurücksetzen</Button>}
            />
          )
        )}
      </div>
    </div>
  );
};

export default Tiere;
